import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const Header = ({ user = null, onLogout = () => {} }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);
  const { language, setLanguage, t } = useLanguage();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      role: 'user'
    }
  ];

  const adminNavigationItems = [
    {
      label: 'Admin',
      path: '/admin-dashboard',
      icon: 'Shield',
      role: 'admin'
    }
  ];

  const isActive = (path) => location?.pathname === path;

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(event?.target)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef?.current && !langMenuRef?.current?.contains(event?.target)) {
        setIsLangMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
        setIsLangMenuOpen(false);
      }
    };

    if (isUserMenuOpen || isMobileMenuOpen || isLangMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen, isMobileMenuOpen, isLangMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user?.name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const visibleNavItems = user?.role === 'admin' 
    ? [...navigationItems, ...adminNavigationItems]
    : navigationItems;

  return (
    <>
      <header className="header-container">
        <div className="header-content">
          <Link to="/" className="header-logo" aria-label="Go to home page">
            <img
              src="/assets/images/logo_LL-1772022913346.jpeg"
              alt="ListingLens logo"
              className="h-10 w-auto rounded-lg object-cover"
              style={{ boxShadow: '0 0 12px rgba(0, 212, 255, 0.4)', maxWidth: '140px', cursor: 'pointer' }}
            />
          </Link>

          {user && (
            <>
              <nav className="header-nav">
                {visibleNavItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`header-nav-link ${isActive(item?.path) ? 'active' : ''}`}
                  >
                    {item?.label}
                  </Link>
                ))}
              </nav>

              <div className="header-actions">
                {/* Language Toggle */}
                <div className="relative" ref={langMenuRef}>
                  <button
                    onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                    style={{
                      background: isLangMenuOpen ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      color: '#6B9EC4',
                    }}
                    aria-label="Select language"
                  >
                    <span>{language === 'bn' ? '🇧🇩' : '🌐'}</span>
                    <span>{language === 'bn' ? 'বাংলা' : 'EN'}</span>
                    <Icon name={isLangMenuOpen ? "ChevronUp" : "ChevronDown"} size={12} color="currentColor" />
                  </button>
                  {isLangMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-36 rounded-xl py-2 z-50" style={{ background: 'rgba(5, 10, 20, 0.98)', border: '1px solid rgba(0, 212, 255, 0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                      <button
                        onClick={() => { setLanguage('en'); setIsLangMenuOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                        style={{ color: language === 'en' ? '#00D4FF' : '#6B9EC4' }}
                      >
                        <span>🌐</span> English
                        {language === 'en' && <span style={{ color: '#00D4FF', marginLeft: 'auto' }}>✓</span>}
                      </button>
                      <button
                        onClick={() => { setLanguage('bn'); setIsLangMenuOpen(false); }}
                        className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                        style={{ color: language === 'bn' ? '#00B450' : '#6B9EC4' }}
                      >
                        <span>🇧🇩</span> বাংলা
                        {language === 'bn' && <span style={{ color: '#00B450', marginLeft: 'auto' }}>✓</span>}
                      </button>
                    </div>
                  )}
                </div>

                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={handleUserMenuToggle}
                    className="user-menu-trigger"
                    aria-label="User menu"
                    aria-expanded={isUserMenuOpen}
                  >
                    <div className="user-avatar">
                      {getUserInitials()}
                    </div>
                    <Icon 
                      name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      color="currentColor" 
                    />
                  </button>

                  {isUserMenuOpen && (
                    <div className="user-menu-dropdown">
                      <div className="user-menu-item" style={{ cursor: 'default' }}>
                        <Icon name="User" size={16} color="currentColor" />
                        <div>
                          <div className="font-medium">{user?.name || 'User'}</div>
                          <div className="text-xs text-muted-foreground">{user?.email || ''}</div>
                        </div>
                      </div>
                      <div className="user-menu-divider" />
                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                        }}
                        className="user-menu-item w-full text-left"
                      >
                        <Icon name="Settings" size={16} color="currentColor" />
                        <span>{t('settings')}</span>
                      </button>
                      <div className="user-menu-divider" />
                      <button
                        onClick={handleLogout}
                        className="user-menu-item w-full text-left"
                      >
                        <Icon name="LogOut" size={16} color="currentColor" />
                        <span>{t('logout')}</span>
                      </button>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleMobileMenuToggle}
                  className="mobile-menu-button"
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                >
                  <Icon 
                    name={isMobileMenuOpen ? "X" : "Menu"} 
                    size={24} 
                    color="currentColor" 
                  />
                </button>
              </div>
            </>
          )}

          {!user && (
            <div className="flex items-center gap-2">
              {/* Language Toggle for non-logged-in state */}
              <div className="relative" ref={langMenuRef}>
                <button
                  onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{
                    background: isLangMenuOpen ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    color: '#6B9EC4',
                  }}
                  aria-label="Select language"
                >
                  <span>{language === 'bn' ? '🇧🇩' : '🌐'}</span>
                  <span>{language === 'bn' ? 'বাংলা' : 'EN'}</span>
                  <Icon name={isLangMenuOpen ? "ChevronUp" : "ChevronDown"} size={12} color="currentColor" />
                </button>
                {isLangMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 w-36 rounded-xl py-2 z-50" style={{ background: 'rgba(5, 10, 20, 0.98)', border: '1px solid rgba(0, 212, 255, 0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    <button
                      onClick={() => { setLanguage('en'); setIsLangMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                      style={{ color: language === 'en' ? '#00D4FF' : '#6B9EC4' }}
                    >
                      <span>🌐</span> English
                      {language === 'en' && <span style={{ color: '#00D4FF', marginLeft: 'auto' }}>✓</span>}
                    </button>
                    <button
                      onClick={() => { setLanguage('bn'); setIsLangMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                      style={{ color: language === 'bn' ? '#00B450' : '#6B9EC4' }}
                    >
                      <span>🇧🇩</span> বাংলা
                      {language === 'bn' && <span style={{ color: '#00B450', marginLeft: 'auto' }}>✓</span>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>
      {isMobileMenuOpen && user && (
        <>
          <div 
            className="mobile-menu-overlay"
            onClick={handleMobileMenuToggle}
            style={{ opacity: isMobileMenuOpen ? 1 : 0 }}
          />
          <div className={`mobile-menu-drawer ${!isMobileMenuOpen ? 'closed' : ''}`}>
            <div className="mobile-menu-header">
              <span className="text-lg font-semibold text-foreground font-heading">{t('more')}</span>
              <button
                onClick={handleMobileMenuToggle}
                className="p-2 rounded-lg hover:bg-muted transition-base"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} color="currentColor" />
              </button>
            </div>

            <nav className="mobile-menu-nav">
              {visibleNavItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={handleMobileMenuToggle}
                  className={`mobile-menu-link ${isActive(item?.path) ? 'active' : ''}`}
                >
                  <Icon name={item?.icon} size={20} color="currentColor" />
                  <span>{item?.label}</span>
                </Link>
              ))}

              <div className="user-menu-divider my-4" />

              {/* Language toggle in mobile drawer */}
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="text-xs" style={{ color: '#6B9EC4' }}>{t('language')}:</span>
                <button
                  onClick={() => setLanguage('en')}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200"
                  style={{
                    background: language === 'en' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: language === 'en' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: language === 'en' ? '#00D4FF' : '#6B9EC4',
                  }}
                >🌐 EN</button>
                <button
                  onClick={() => setLanguage('bn')}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200"
                  style={{
                    background: language === 'bn' ? 'rgba(0, 180, 80, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: language === 'bn' ? '1px solid rgba(0, 180, 80, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: language === 'bn' ? '#00B450' : '#6B9EC4',
                  }}
                >🇧🇩 বাংলা</button>
              </div>

              <div className="user-menu-divider my-4" />

              <button
                onClick={() => {
                  handleMobileMenuToggle();
                }}
                className="mobile-menu-link"
              >
                <Icon name="Settings" size={20} color="currentColor" />
                <span>{t('settings')}</span>
              </button>

              <button
                onClick={() => {
                  handleMobileMenuToggle();
                  handleLogout();
                }}
                className="mobile-menu-link"
              >
                <Icon name="LogOut" size={20} color="currentColor" />
                <span>{t('logout')}</span>
              </button>
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Header;